<template>
  <div style="padding: 20px;">
    <p v-if="generationError" role="alert">{{ generationError }}</p>
    <div v-if="showFirstMenuOnboarding" class="daily-preferences-backdrop first-menu-onboarding">
      <div class="daily-preferences-modal">
        <div class="week-kicker">AVANT MES PREMIERS MENUS</div>
        <h2>Je peux personnaliser ma semaine avant de commencer</h2>
        <p>
          MMM peut adapter mes menus à mes habitudes : petit-déjeuner, viande et poisson,
          organisation des repas, aliments à exclure, activité sportive et autres préférences.
          Je peux les régler maintenant ou les modifier plus tard.
        </p>
        <div class="daily-preferences-actions">
          <button
            type="button"
            class="button"
            :disabled="firstMenuOnboardingSaving"
            @click="chooseFirstMenuPreferences"
          >
            Personnaliser mes préférences
          </button>
          <button
            type="button"
            class="daily-secondary"
            :disabled="firstMenuOnboardingSaving"
            @click="continueToFirstMenus"
          >
            {{ firstMenuOnboardingSaving ? "Enregistrement..." : "Créer mes menus maintenant" }}
          </button>
        </div>
      </div>
    </div>
    <div
      style="display: flex; justify-content: space-between; flex-wrap: wrap;"
      :class="{ disabled: menus === 'preferences_too_restrictive' }"
    >
      <div class="scale-selector" :class="{ disabled: menus === 'preferences_too_restrictive' }">
        <div class="scale-selector-day" @click="gotoDailyMenu">Jour</div>
        <div class="scale-selector-week active">Semaine</div>
      </div>

      <div class="day-selector" :class="{ disabled: menus === 'preferences_too_restrictive' }">
        <div class="day-selector-arrow" @click="gotoPreviousWeek">
          <font-awesome-icon class="icon" :icon="['fas', 'chevron-left']" />
        </div>

        <div class="day-selector-previous-day week-label">
          <span>{{ weekRangeLabel(prevWeekLabel) }}</span>
          <small>Semaine {{ prevWeekLabel }}</small>
        </div>
        <div class="day-selector-current-day week-label">
          <span>{{ weekRangeLabel(weekNumber) }}</span>
          <small>Semaine {{ weekNumber }}</small>
        </div>
        <div class="day-selector-next-day week-label">
          <span>{{ weekRangeLabel(nextWeekLabel) }}</span>
          <small>Semaine {{ nextWeekLabel }}</small>
        </div>

        <div class="day-selector-arrow" @click="gotoNextWeek">
          <font-awesome-icon class="icon" :icon="['fas', 'chevron-right']" />
        </div>
      </div>

      <div class="scale-selector" style="visibility: hidden;">
        <div class="scale-selector-day">Jour</div>
        <div class="scale-selector-week active">Semaine</div>
      </div>
    </div>

    <div v-if="!loadingPat && patData" class="pat-section">
      <h2 class="pat-title">
        Retrouvez vos actualités locales avec {{ patData.name }}
      </h2>

      <div class="pat-row" v-if="patData.title_1 || patData.text_1">
        <div class="pat-content">
          <a href="/actualites-pat#actulaite-pat-1">{{ patData.title_1 }}</a>
        </div>
      </div>

      <div class="pat-row" v-if="patData.title_2 || patData.text_2">
        <div class="pat-content">
          <a href="/actualites-pat#actulaite-pat-2">{{ patData.title_2 }}</a>
        </div>
      </div>

      <div class="pat-row" v-if="patData.title_3 || patData.text_3">
        <div class="pat-content">
          <a href="/actualites-pat#actulaite-pat-3">{{ patData.title_3 }}</a>
        </div>
      </div>
    </div>

    <div style="margin-top: 20px;">
      <LoaderView v-if="menus == 'loading' || menus == null"></LoaderView>

      <div v-else-if="menus == 'error'">
        <font-awesome-icon class="icon" :icon="['fas', 'exclamation-triangle']" />
      </div>

      <div v-else-if="menus == 'consumer_profil_not_available'">
        Vous n'avez pas encore rempli votre profil de meilleur mangeur
      </div>

      <div v-else-if="menus == 'preferences_too_restrictive'">
        Mes critères de composition des menus sont trop restrictifs et ne permettent pas à Mes Meilleurs Menus de me
        proposer un programme de menus : est-il possible de simplifier
        <button type="button" class="week-preferences-text-link" @click="gotoPreferences">Mes préférences</button>
        ?<br />
        (Temps maximum pour cuisiner, trop d'ingrédients exclus ...)
      </div>

      <div v-else-if="menus == 'not_yet_available'">
        Nous n'avons pas encore décidé de votre menu pour cette semaine
      </div>

      <div v-else class="week-dashboard">
        <div class="personalized-week-kicker">MES MENUS PERSONNALISÉS DE LA SEMAINE</div>
        <div class="week-preferences-shortcut">
          <button type="button" class="button" aria-haspopup="dialog" :aria-expanded="showWeekPreferences" @click="showWeekPreferences = true">Mes préférences pour la semaine</button>
          <button type="button" class="button" aria-haspopup="dialog" :aria-expanded="weekTool === 'nutrition'" @click="weekTool = 'nutrition'">Mes apports nutritionnels</button>
          <button type="button" class="button" aria-haspopup="dialog" :aria-expanded="weekTool === 'shopping'" @click="weekTool = 'shopping'">Mes courses</button>
          <div v-if="mealPrepOptionEnabled" class="week-meal-prep-actions">
            <button type="button" class="button week-meal-prep-link" @click="openMealPrepPlanner">Ma cuisine à l’avance</button>
            <button v-if="mealPrepSelectionReady" type="button" class="button week-meal-prep-plan-link" @click="openMealPrepPlanner(true)">Voir mon plan de préparation</button>
          </div>
        </div>

        <div class="week-refresh-panel">
          <button type="button" class="week-refresh-action" :class="{ 'is-pending': weekRefreshPending }" :disabled="refreshingWeek" @click="refreshSelectedWeek">
            <font-awesome-icon :icon="['fas', 'arrows-rotate']" aria-hidden="true" />
            {{ refreshingWeek ? 'Mise à jour…' : 'Mettre à jour ma semaine de menus' }}
          </button>
          <span v-if="refreshingWeek">{{ deepSearchRunning ? 'Recherche approfondie en cours… cela peut prendre jusqu’à 2 minutes.' : applyingSavedPreferences ? 'Je recalcule la semaine avec vos préférences enregistrées… quelques dizaines de secondes.' : 'Recherche d’un meilleur équilibre… jusqu’à 45 secondes.' }}</span>
          <span v-else>Après avoir modifié vos préférences, cliquez ici pour générer une nouvelle semaine avec vos choix. Sinon, MMM met à jour les menus selon les changements effectués.</span>
          <p v-if="weekRefreshMessage" role="status">{{ weekRefreshMessage }}</p>
          <button v-if="deepSearchAvailable" type="button" class="button" :disabled="refreshingWeek" @click="refreshSelectedWeek(true)">{{ deepSearchRunning ? 'Recherche approfondie…' : 'Lancer une recherche approfondie (jusqu’à 2 min)' }}</button>
        </div>
        <div v-if="mealPrepWeekReady || mealPrepRollbackAvailable || mealPrepRollbackMessage" class="meal-prep-rollback-banner">
          <p v-if="mealPrepWeekReady"><strong>Votre nouvelle semaine est prête.</strong> Les repas choisis ont été ajustés pour faciliter leur préparation ensemble.</p>
          <p v-else-if="mealPrepRollbackAvailable">Vous pouvez consulter votre semaine ou revenir à la semaine précédente.</p>
          <div class="meal-prep-rollback-actions">
            <button v-if="mealPrepWeekReady" type="button" class="button" @click="scrollToNewMealPrepWeek">
              Voir la nouvelle semaine
            </button>
            <button v-if="mealPrepOptionEnabled && !mealPrepSelectionReady && (mealPrepWeekReady || mealPrepRollbackAvailable)" type="button" class="button" @click="openMealPrepPlanner">
              Voir le plan de préparation des repas à l’avance
            </button>
            <button v-if="mealPrepRollbackAvailable" type="button" class="button" :disabled="mealPrepRollbackBusy" @click="restorePreviousMealPrepWeek">
              {{ mealPrepRollbackBusy ? "Restauration…" : "Revenir à la semaine précédente" }}
            </button>
          </div>
          <p v-if="mealPrepRollbackMessage" role="status">{{ mealPrepRollbackMessage }}</p>
        </div>

        <div class="week-planner">
          <div
            v-for="dailyMenu of menus"
            :key="dailyMenu.date"
            class="week-day"
          >
            <div class="week-day-header">{{ compactDate(dailyMenu.date) }}</div>

            <div
              v-for="group in getDisplayMealGroupsFromDailyMenu(dailyMenu)"
              :key="`${dailyMenu.date}-${group.slot}`"
              class="compact-meal-group"
              :class="{ 'compact-meal-group--empty': !group.cards.length }"
            >
              <div
                v-for="(card, index) in group.cards"
                :key="`${dailyMenu.date}-${group.slot}-${card.type}-${card.item?.id || index}`"
                class="compact-meal"
                :class="{ 'compact-meal-outside': card.type === 'outside' }"
                @click="card.type === 'item' && gotoMealCard(card.item, dailyMenu.date)"
              >
                <template v-if="card.type === 'item'">
                  <div class="compact-meal-type">{{ card.item.dish_type_display }}</div>
                  <div class="compact-meal-content" :class="{ 'with-photo': !!card.item.dish.photo }">
                    <img
                      v-if="card.item.dish.photo"
                      :src="mediaSrc(card.item.dish.photo)"
                      :alt="card.item.dish.name"
                      class="compact-meal-photo"
                      loading="lazy"
                    />
                    <div v-else class="compact-meal-photo compact-meal-photo--placeholder" aria-hidden="true">
                      <font-awesome-icon :icon="['fas', 'utensils']" />
                    </div>
                    <div class="compact-meal-name">{{ displayMenuDishName(card.item) }}</div>
                    <div
                      v-if="['lunch_main', 'diner_main'].includes(card.item.dish_type) && card.item.dish.mealim_score != null"
                      class="compact-mealim-score"
                    >
                      <span>MEALIM</span>
                      <strong>{{ card.item.dish.mealim_score }}/100</strong>
                    </div>
                  </div>
                  <div v-if="card.mealBadgeType" class="compact-badge">
                    {{ card.mealBadgeType === 'leftovers' ? 'Restes' : 'Prévu pour 2 repas' }}
                  </div>
                  <div
                    v-for="adaptation in getSportAdaptations(card.item)"
                    :key="`sport-${card.item.id}-${adaptation.consumer_id}`"
                    class="compact-sport-badge"
                  >
                    {{ sportContextLabel(adaptation) }}
                  </div>
                  <div class="compact-actions">
                    <button type="button" @click.stop="gotoMealCard(card.item, dailyMenu.date)">Voir</button>
                    <button type="button" class="compact-change" @click.stop="openReplaceModal(card.item, dailyMenu.date)">Modifier</button>
                  </div>
                </template>
                <template v-else>
                  <div class="compact-meal-type">{{ card.label }}</div>
                  <div class="compact-meal-content with-photo">
                    <img :src="burgerImage" alt="Repas à l'extérieur" class="compact-meal-photo" loading="lazy" />
                    <div class="compact-meal-name">Repas à l'extérieur</div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>

        <div class="week-help">
          <strong>Besoin de plus de détails ?</strong>
          La vue <button type="button" @click="gotoDailyMenu">Jour</button> conserve les photos, les recettes et les actions détaillées repas par repas.
        </div>

        <!-- Repas personnalisés ponctuels -->
        <div ref="pantrySection" class="pantry-tool">
          <div class="pantry-tool-heading">
            <div>
              <strong>J’ai déjà des légumes ou des aliments à utiliser ?</strong>
              <span>Je les indique à MMM avec une quantité approximative. Ils seront intégrés à ma semaine lorsqu’une recette compatible existe.</span>
            </div>
            <button v-if="!pantryExpanded" type="button" @click="pantryExpanded = true">Ajouter mes aliments</button>
          </div>

          <div v-if="pantryExpanded" class="pantry-body">
            <div class="pantry-table">
              <div class="pantry-table-head">
                <strong>Aliment</strong>
                <strong>Quantité approximative / forme</strong>
              </div>

              <div
                v-for="(row, index) in pantryRows"
                :key="row.key"
                class="pantry-table-row"
              >
                <div class="pantry-food-cell">
                  <div class="pantry-search-box">
                    <input
                      v-model="row.search"
                      type="text"
                      :class="{ selected: !!row.aliment_id }"
                      placeholder="Ex. courgette"
                      @input="searchPantryRow(index)"
                      @focus="row.aliment_id && (row.search = row.name)"
                      @keydown.esc="row.suggestions = []"
                    />
                    <div v-if="row.suggestions.length" class="pantry-suggestions">
                      <button
                        v-for="suggestion in row.suggestions"
                        :key="suggestion.id"
                        type="button"
                        @click="selectPantrySuggestion(index, suggestion)"
                      >
                        <span>{{ suggestion.name }}</span>
                        <small v-if="suggestion.season?.status === 'in_season'">✓ de saison</small>
                        <small v-else-if="suggestion.season?.status === 'out_of_season_fresh'">hors saison en frais</small>
                      </button>
                    </div>
                  </div>
                  <small v-if="row.aliment_id" class="pantry-selected-food">✓ {{ row.name }}</small>
                </div>

                <div class="pantry-details-cell">
                  <input
                    v-model.number="row.quantity"
                    type="number"
                    @input="pantryValidated = false; pantryItems = []"
                    min="0"
                    step="0.1"
                    aria-label="Quantité approximative"
                  />
                  <select v-model="row.unit" aria-label="Unité" @change="pantryValidated = false; pantryItems = []">
                    <option value="g">g</option>
                    <option value="kg">kg</option>
                    <option value="piece">pièce(s)</option>
                    <option value="can">boîte(s)</option>
                  </select>
                  <select v-model="row.form" aria-label="Forme" @change="pantryValidated = false; pantryItems = []">
                    <option value="fresh">Frais</option>
                    <option value="frozen">Surgelé</option>
                    <option value="canned">En conserve</option>
                  </select>
                  <button
                    v-if="pantryRows.length > 1"
                    type="button"
                    class="pantry-remove-row"
                    aria-label="Supprimer cet aliment"
                    @click="removePantryRow(index)"
                  >×</button>
                </div>

                <div
                  v-if="row.aliment_id && pantrySeasonMessage(row)"
                  class="pantry-season"
                  :class="{ warning: pantrySeasonWarning(row), ok: !pantrySeasonWarning(row) }"
                >
                  {{ pantrySeasonMessage(row) }}
                </div>
              </div>
            </div>

            <div class="pantry-row-actions">
              <button type="button" class="pantry-add-row" @click="addPantryRow">+ Ajouter un aliment</button>
              <button
                type="button"
                class="pantry-validate"
                :disabled="!canValidatePantry"
                @click="validatePantryItems"
              >
                Valider mes aliments
              </button>
            </div>

            <div v-if="pantryValidationError" class="pantry-validation-error">
              {{ pantryValidationError }}
            </div>

            <div class="pantry-season-reminder">
              <strong>Le bon réflexe MMM :</strong>
              pour les achats frais, je privilégie les légumes et fruits de saison. Si j’ai déjà un produit hors saison, mieux vaut l’utiliser que le gaspiller ; surgelés et conserves restent tout à fait utilisables hors saison.
            </div>

            <template v-if="pantryValidated">
              <div class="pantry-validated-summary">
                <strong>{{ pantryItems.length }} aliment{{ pantryItems.length > 1 ? "s" : "" }} validé{{ pantryItems.length > 1 ? "s" : "" }}</strong>
                <span>Je peux maintenant choisir comment MMM les intègre à ma semaine.</span>
              </div>

              <div class="pantry-modes">
                <label :class="{ active: pantryMode === 'adapt' }">
                  <input v-model="pantryMode" type="radio" value="adapt" />
                  <span><strong>Adapter ma semaine actuelle</strong>MMM remplace seulement quelques repas lorsque c’est pertinent.</span>
                </label>
                <label :class="{ active: pantryMode === 'recompose' }">
                  <input v-model="pantryMode" type="radio" value="recompose" />
                  <span><strong>Recomposer ma semaine</strong>MMM reconstruit la semaine puis donne la priorité aux aliments que j’ai déjà.</span>
                </label>
              </div>

              <div class="pantry-actions">
                <button type="button" class="button" :disabled="pantrySaving" @click="applyPantryWeek">
                  {{ pantrySaving ? "Adaptation en cours…" : "Adapter mes menus" }}
                </button>
                <button type="button" class="pantry-secondary" @click="resetPantryTool">Annuler</button>
              </div>
            </template>

            <div v-if="pantryResult" class="pantry-result">
              <strong>{{ pantryResult.message }}</strong>
              <div v-if="pantryResult.replacements?.length" class="pantry-result-list">
                <span v-for="(change, index) in pantryResult.replacements" :key="index">
                  {{ formatPantryDate(change.date) }} · {{ pantryMealLabel(change.meal) }} :
                  <b>{{ change.new_recipe }}</b> avec {{ change.aliment }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="starter-callout preferences-callout">
          <div>
            <strong>Je peux adapter mes menus à mes habitudes.</strong>
            <span>
              Type d’alimentation, entrées ou non, organisation des repas, viande et poisson, aliments à exclure… je peux modifier mes préférences à tout moment.
            </span>
          </div>
          <button type="button" @click="gotoPreferences">Modifier mes préférences</button>
        </div>

      </div>
    </div>

    <ReplaceDishModal
      v-if="itemToReplace"
      :item="itemToReplace.item"
      :date="itemToReplace.date"
      @close="closeReplaceModal"
      @update="handleReplacement"
    />

    <div v-if="Array.isArray(menus)" class="bottom-cards">
      <MyPdf />
      <MyLikedDishes />

    </div>

    <div v-if="weekTool" class="week-preferences-overlay" :class="{ 'nutrition-fullscreen-overlay': weekTool === 'nutrition' }" @click.self="weekTool = null" @keydown.esc="weekTool = null">
      <section class="week-preferences-dialog" :class="{ 'nutrition-fullscreen-dialog': weekTool === 'nutrition' }" role="dialog" aria-modal="true" aria-labelledby="week-tool-title">
        <header class="week-preferences-header">
          <h2 id="week-tool-title">{{ weekTool === 'nutrition' ? 'Mes apports nutritionnels' : 'Mes courses' }}</h2>
          <button type="button" class="button gray" @click="weekTool = null">Fermer ×</button>
        </header>
        <Apports v-if="weekTool === 'nutrition'" :key="'nutrition-' + weekToolsRevision" embedded @preferences="openPreferencesFromNutrition" :start-date="weekStartIso(weekNumber)" />
        <MyShoppingList v-else :key="'shopping-' + weekToolsRevision" embedded :week-number="Number(weekNumber)" />
      </section>
    </div>
    <div v-if="showWeekPreferences" class="week-preferences-overlay" @click.self="closeWeekPreferences">
      <section class="week-preferences-dialog" role="dialog" aria-modal="true" aria-labelledby="week-preferences-title">
        <header class="week-preferences-header">
          <h2 id="week-preferences-title">Mes préférences pour la semaine</h2>
          <button type="button" class="button gray" @click="closeWeekPreferences">Fermer ×</button>
        </header>
        <MesPreferences ref="weekPreferences" embedded @menus-updated="refreshAfterPreferences" @pantry-request="openPantryFromPreferences" @meal-prep-request="openMealPrepFromPreferences" />
      </section>
    </div>
    <div v-if="mealPrepRecalculationBusy" class="meal-prep-recalculation-overlay" role="status" aria-live="polite" aria-busy="true">
      <div class="meal-prep-recalculation-card">
        <span class="meal-prep-recalculation-spinner" aria-hidden="true"></span>
        <h2>Votre semaine est en cours de recalcul</h2>
        <p>MMM recherche les meilleurs ajustements pour préparer vos repas ensemble.</p>
        <p>Le calcul peut prendre plus d’une minute. Gardez cette page ouverte ; nous afficherons la nouvelle semaine dès qu’elle sera prête.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { api } from "@/api";
import LoaderView from "@/components/LoaderView.vue";
import MyLikedDishes from "@/views/MyLikedDishes.vue";
import MyPdf from "@/views/MyPdf.vue";
import ReplaceDishModal from "@/components/ReplaceDishModal.vue";
import { userState } from "@/utils/store";
import MesPreferences from "@/views/MesPreferences.vue";
import Apports from "@/views/Apports.vue";
import MyShoppingList from "@/views/MyShoppingList.vue";

// Cache mémoire conservé pendant la navigation. Chaque entrée est rattachée
// à l'identité du foyer afin de ne jamais afficher le menu d'une session
// précédente pendant le chargement de la session courante.
const weekMenusCache = Object.create(null);

function getHouseholdCacheKey() {
  const me = userState.me || {};
  const home = me.consumer_account || {};
  return String(home.id || me.id || "anonymous");
}

function getWeekCacheKey(weekNumber) {
  return `${getHouseholdCacheKey()}:${userState.menuRevision || 0}:${weekNumber}`;
}

function getCachedWeek(weekNumber) {
  return weekMenusCache[getWeekCacheKey(weekNumber)];
}

function setCachedWeek(weekNumber, menus) {
  weekMenusCache[getWeekCacheKey(weekNumber)] = menus;
}

export default {
  name: "MyMenusWeek",
  components: {
    LoaderView,
    MyLikedDishes,
    MyPdf,
    ReplaceDishModal,
    MesPreferences,
    Apports,
    MyShoppingList,
  },

  data() {
    return {
      year: new Date().getFullYear(),
      weekNumber: this.getCurrentWeekNumber(),
      baseWeekNumber: null,
      menus: null,
      nextWeekMenusCache: [],
      patData: null,
      loadingPat: true,
      burgerImage: "/images/repas-exterieur.jpg",
      itemToReplace: null,
      refreshingWeek: false,
      applyingSavedPreferences: false,
      deepSearchAvailable: false,
      deepSearchRunning: false,
      generationError: "",
      protectedChoicesByWeek: {},
      weekRefreshPending: false,
      weekRefreshMessage: "",
      mealPrepRollbackAvailable: false,
      mealPrepRollbackBusy: false,
      mealPrepRollbackMessage: "",
      mealPrepRecalculationBusy: false,
      mealPrepWeekReady: false,
      mealPrepSelectionReady: false,
      weekToolsRevision: 0,
      showFirstMenuOnboarding: false,
      showWeekPreferences: false,
      weekTool: null,
      firstMenuOnboardingSaving: false,
      batchPlan: null,
      pantryExpanded: false,
      pantryRows: [
        {
          key: 1,
          search: "",
          suggestions: [],
          aliment_id: null,
          name: "",
          quantity: 500,
          unit: "g",
          form: "fresh",
          season: null,
          timer: null,
        },
      ],
      pantryNextRowKey: 2,
      pantryItems: [],
      pantryValidated: false,
      pantryValidationError: "",
      pantryMode: "adapt",
      pantrySaving: false,
      pantryResult: null,
    };
  },

  computed: {
    mealPrepOptionEnabled() { return !!userState.me?.consumer_account?.meal_prep_grouping_enabled; },
    batchHouseholdKey() { return getHouseholdCacheKey(); },
    canValidatePantry() {
      return (
        this.pantryRows.length > 0 &&
        this.pantryRows.every(
          row => row.aliment_id && Number(row.quantity) > 0
        )
      );
    },
    weeksInYear() {
      const y = this.year;
      return this.getCurrentWeekNumber(new Date(y, 11, 28));
    },
    prevWeekLabel() {
      return this.weekNumber === 1 ? this.weeksInYear : this.weekNumber - 1;
    },
    nextWeekLabel() {
      return this.weekNumber === this.weeksInYear ? 1 : this.weekNumber + 1;
    },
    starterPreference() {
      return userState.me?.consumer_account?.starter_preference || "lunch";
    },

  },

  mounted() {
    this.refreshMealPrepSelectionStatus();
    this.baseWeekNumber = this.weekNumber;

    const cachedMenus = getCachedWeek(this.weekNumber);
    this.menus = cachedMenus || "loading";

    // Les menus et les actualités sont indépendants : on ne bloque pas l’un sur l’autre.
    this.checkNextTwoWeeks();
    this.loadMealPrepRollbackStatus(this.weekNumber);
    this.fetchPat();
    this.initFirstMenuOnboarding();
    this.loadBatchCookingPlan();
  },

  activated() { this.refreshMealPrepSelectionStatus(); },

  beforeRouteLeave(to, from, next) {
    if (this.showWeekPreferences && this.$refs.weekPreferences) this.$refs.weekPreferences.requestLeave(next);
    else next();
  },

  methods: {
    refreshMealPrepSelectionStatus() {
      const count = Number(userState.me?.consumer_account?.meal_prep_group_size) || 3;
      const key = "mmm-meal-prep-v3:" + getHouseholdCacheKey() + ":" + this.weekNumber;
      try {
        const selected = JSON.parse(localStorage.getItem(key) || "[]");
        this.mealPrepSelectionReady = this.mealPrepOptionEnabled && Array.isArray(selected) && selected.length === count;
      } catch (_) {
        this.mealPrepSelectionReady = false;
      }
    },
    openMealPrepPlanner(viewPlan = false) {
      const count = Number(userState.me?.consumer_account?.meal_prep_group_size) || 3;
      const query = { week: String(this.weekNumber), count: String(count) };
      if (viewPlan) query.viewPlan = "1";
      this.$router.push({ name: "my-batch-cooking", query });
    },
    openPreferencesFromNutrition() {
      this.weekTool = null;
      this.showWeekPreferences = true;
    },
    closeWeekPreferences() {
      const preferences = this.$refs.weekPreferences;
      if (!preferences) { this.showWeekPreferences = false; return; }
      preferences.requestLeave(allowed => { if (allowed !== false) this.showWeekPreferences = false; });
    },
    openMealPrepFromPreferences() {
      const count = Number(userState.me?.consumer_account?.meal_prep_group_size) || 3;
      this.showWeekPreferences = false;
      this.$router.push({ name: "my-batch-cooking", query: { week: String(this.weekNumber), count: String(count) } });
    },
    openPantryFromPreferences() {
      const preferences = this.$refs.weekPreferences;
      if (!preferences) {
        this.showWeekPreferences = false;
        this.$nextTick(() => this.scrollToPantry());
        return;
      }
      preferences.requestLeave(allowed => {
        if (allowed === false) return;
        this.showWeekPreferences = false;
        this.$nextTick(() => this.scrollToPantry());
      });
    },
    async refreshAfterPreferences() {
      this.showWeekPreferences = false;
      this.mealPrepRollbackMessage = "";
      try {
        await Promise.all([
          this.loadMenus(this.weekNumber),
          this.loadBatchCookingPlan(),
          this.loadMealPrepRollbackStatus(this.weekNumber),
        ]);
        this.$nextTick(() => window.scrollTo({ top: 0, behavior: "smooth" }));
      } finally {
        this.mealPrepRecalculationBusy = false;
      }
    },
    scrollToNewMealPrepWeek() {
      const week = this.$el.querySelector(".week-planner");
      if (week) week.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    async loadMealPrepRollbackStatus(weekNumber) {
      try {
        const response = await api.get("/meal-prep-week-revision/");
        this.mealPrepRollbackAvailable = !!response.data?.available
          && response.data?.week_start === this.weekStartIso(weekNumber);
      } catch (error) {
        this.mealPrepRollbackAvailable = false;
      }
    },
    async restorePreviousMealPrepWeek() {
      if (!this.mealPrepRollbackAvailable || this.mealPrepRollbackBusy) return;
      const confirmed = window.confirm("Restaurer la semaine de menus précédente ? Les modifications faites sur la nouvelle semaine seront remplacées.");
      if (!confirmed) return;
      this.mealPrepRollbackBusy = true;
      this.mealPrepRollbackMessage = "";
      try {
        const response = await api.post("/restore-previous-meal-prep-week/");
        const account = userState.me?.consumer_account;
        if (account && response.data?.success) {
          account.meal_prep_grouping_enabled = !!response.data.meal_prep_grouping_enabled;
          account.meal_prep_group_size = Number(response.data.meal_prep_group_size) || 3;
        }
        userState.menuRevision += 1;
        this.mealPrepRollbackAvailable = false;
        await this.loadMenus(this.weekNumber);
        this.mealPrepRollbackMessage = "La semaine précédente a été restaurée.";
      } catch (error) {
        this.mealPrepRollbackMessage = error.response?.data?.detail || "La restauration a échoué. La semaine affichée est conservée.";
      } finally {
        this.mealPrepRollbackBusy = false;
      }
    },

    async loadBatchCookingPlan() {
      const home = userState.me?.consumer_account;
      if (!home?.batch_cooking_enabled) {
        this.batchPlan = null;
        return;
      }
      try {
        const response = await api.get(`/batch-cooking-plan/${this.weekNumber}/`);
        this.batchPlan = response.data;
      } catch (error) {
        console.error("Impossible de charger le plan de batch cooking :", error);
        this.batchPlan = null;
      }
    },

    batchSessionLabel(session) {
      const day = {
        monday: "Lundi",
        tuesday: "Mardi",
        wednesday: "Mercredi",
        thursday: "Jeudi",
        friday: "Vendredi",
        saturday: "Samedi",
        sunday: "Dimanche",
      }[session.day] || session.day || "";
      const period = {
        morning: "matin",
        afternoon: "après-midi",
        evening: "soir",
      }[session.period] || session.period || "";
      return `${day} ${period}`.trim();
    },

    batchMealLabel(meal) {
      const date = new Date(`${meal.date}T12:00:00`);
      const day = date.toLocaleDateString("fr-FR", { weekday: "long" });
      const mealLabel = meal.meal === "lunch" ? "déjeuner" : "dîner";
      return `${day} ${mealLabel}`;
    },

    formatBatchQuantity(ingredient) {
      const quantity = Number(ingredient.quantity || 0);
      const rounded = Number.isInteger(quantity)
        ? String(quantity)
        : quantity.toFixed(1).replace(".", ",");
      const unit = ingredient.unit === "piece" ? "pièce(s)" : ingredient.unit;
      return `${rounded} ${unit}`;
    },

    displayMenuDishName(item) {
      const name = String(item?.dish?.name || "").trim();
      if (!["snack", "morning_snack"].includes(item?.dish_type)) return name;

      const withoutQuantity =
        name.replace(/^\s*\d+(?:[.,]\d+)?\s*(?:x|×)?\s*/i, "").trim() || name;

      // Un en-cas est ensuite quantifié en grammes selon les besoins du foyer.
      // On évite donc un titre fixe comme « 1 Poire » et on indique que plusieurs
      // unités peuvent être nécessaires.
      if (/^\d/.test(name) && !/\(s\)$/i.test(withoutQuantity)) {
        return `${withoutQuantity}(s)`;
      }
      return withoutQuantity;
    },

    scrollToPantry() {
      this.pantryExpanded = true;
      this.$nextTick(() => {
        this.$refs.pantrySection?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    },

    addPantryRow() {
      this.pantryRows.push({
        key: this.pantryNextRowKey++,
        search: "",
        suggestions: [],
        aliment_id: null,
        name: "",
        quantity: 500,
        unit: "g",
        form: "fresh",
        season: null,
        timer: null,
      });
      this.pantryValidated = false;
      this.pantryValidationError = "";
      this.pantryResult = null;
    },

    removePantryRow(index) {
      const row = this.pantryRows[index];
      if (row?.timer) window.clearTimeout(row.timer);
      this.pantryRows.splice(index, 1);
      this.pantryValidated = false;
      this.pantryItems = [];
      this.pantryValidationError = "";
      this.pantryResult = null;
    },

    searchPantryRow(index) {
      const row = this.pantryRows[index];
      if (!row) return;

      if (row.timer) window.clearTimeout(row.timer);
      row.aliment_id = null;
      row.name = "";
      row.season = null;
      this.pantryValidated = false;
      this.pantryItems = [];
      this.pantryValidationError = "";
      this.pantryResult = null;

      const q = String(row.search || "").trim();
      if (q.length < 2) {
        row.suggestions = [];
        return;
      }

      row.timer = window.setTimeout(async () => {
        try {
          const response = await api.get("/aliments/", { params: { q } });
          const alreadySelected = new Set(
            this.pantryRows
              .filter((item, rowIndex) => rowIndex !== index && item.aliment_id)
              .map(item => item.aliment_id)
          );
          row.suggestions = (response.data || [])
            .filter(item => !alreadySelected.has(item.id))
            .slice(0, 8);
        } catch (error) {
          console.error("Impossible de rechercher les aliments :", error);
          row.suggestions = [];
        }
      }, 180);
    },

    selectPantrySuggestion(index, suggestion) {
      const row = this.pantryRows[index];
      if (!row || !suggestion) return;
      row.aliment_id = suggestion.id;
      row.name = suggestion.name;
      row.search = suggestion.name;
      row.season = suggestion.season || null;
      row.suggestions = [];
      this.pantryValidated = false;
      this.pantryItems = [];
      this.pantryValidationError = "";
      this.pantryResult = null;
    },

    validatePantryItems() {
      if (!this.canValidatePantry) {
        this.pantryValidationError =
          "Choisissez un aliment dans la liste proposée et indiquez une quantité pour chaque ligne.";
        return;
      }

      this.pantryItems = this.pantryRows.map(row => ({
        aliment_id: row.aliment_id,
        name: row.name,
        quantity: Number(row.quantity),
        unit: row.unit,
        form: row.form,
        season: row.season,
      }));
      this.pantryValidated = true;
      this.pantryValidationError = "";
      this.pantryResult = null;
    },

    pantrySeasonWarning(item) {
      return item.form === "fresh" && item.season?.status === "out_of_season_fresh";
    },

    pantrySeasonMessage(item) {
      if (item.form === "frozen") {
        return "Surgelé : c’est tout à fait possible hors saison. Pour les achats frais, MMM privilégie les produits de saison.";
      }
      if (item.form === "canned") {
        return "En conserve : c’est tout à fait possible hors saison. Pour les achats frais, MMM privilégie les produits de saison.";
      }
      if (item.season?.status === "in_season") return "✓ De saison actuellement.";
      if (item.season?.status === "out_of_season_fresh") {
        return "⚠ Ce produit frais n’est pas de saison actuellement. Puisque vous l’avez déjà, mieux vaut l’utiliser que le gaspiller.";
      }
      return "";
    },

    pantryMealLabel(meal) {
      return { breakfast: "Petit déjeuner", morning_snack: "En-cas du matin", lunch: "Déjeuner", snack: "En-cas de l’après-midi", dinner: "Dîner" }[meal] || "Repas";
    },

    formatPantryDate(value) {
      if (!value) return "";
      const d = new Date(value + "T12:00:00");
      return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "short" });
    },

    resetPantryTool() {
      this.pantryExpanded = false;
      this.pantryRows = [{
        key: this.pantryNextRowKey++,
        search: "",
        suggestions: [],
        aliment_id: null,
        name: "",
        quantity: 500,
        unit: "g",
        form: "fresh",
        season: null,
        timer: null,
      }];
      this.pantryItems = [];
      this.pantryValidated = false;
      this.pantryValidationError = "";
      this.pantryMode = "adapt";
      this.pantryResult = null;
    },

    async applyPantryWeek() {
      if (!this.pantryItems.length) return;
      this.pantrySaving = true;
      this.pantryResult = null;
      try {
        const response = await api.post("/pantry-week/", {
          week_number: this.weekNumber,
          mode: this.pantryMode,
          items: this.pantryItems.map(item => ({
            aliment_id: item.aliment_id,
            quantity: item.quantity,
            unit: item.unit,
            form: item.form,
          })),
        });
        this.pantryResult = response.data;

        // Le cache semaine doit être remplacé par la semaine réellement modifiée.
        const menuResponse = await api.get(`my-menus/${this.weekNumber}/`);
        this.menus = menuResponse.data;
        if (Array.isArray(this.menus)) {
          setCachedWeek(this.weekNumber, this.menus);
        }
      } catch (error) {
        console.error("Impossible d’adapter la semaine avec les aliments disponibles :", error);
        this.pantryResult = {
          message: error?.response?.data?.detail || "MMM n’a pas pu adapter la semaine avec ces aliments.",
          replacements: [],
        };
      } finally {
        this.pantrySaving = false;
      }
    },

    sportLoadLabel(level) {
      return {
        rest: "Repos",
        light: "Charge légère",
        moderate: "Charge modérée",
        high: "Forte charge",
        competition: "Compétition",
      }[level] || "—";
    },

    sportContextLabel(adaptation) {
      if (!adaptation) return "";
      const who = adaptation.consumer_name ? `${adaptation.consumer_name} · ` : "";
      if (adaptation.context === "before") return `${who}portion glucidique renforcée · avant séance`;
      if (adaptation.context === "after") return `${who}portion glucidique renforcée · après séance`;
      return `${who}portion glucidique renforcée autour de la séance`;
    },

    sportSnackLabel(snack) {
      return {
        pre_or_post_training: "Collation autour de l’entraînement à prévoir",
        consider_around_training: "Collation à envisager selon l’horaire",
      }[snack] || "";
    },

    initFirstMenuOnboarding() {
      const home = userState.me?.consumer_account;
      const hasProfile = Array.isArray(home?.consumers) && home.consumers.length > 0;
      this.showFirstMenuOnboarding =
        !!home &&
        hasProfile &&
        home.first_menu_onboarding_completed === false;
    },

    async completeFirstMenuOnboarding() {
      if (!userState.me?.consumer_account) return;
      await userState.savePreferences(
        { first_menu_onboarding_completed: true },
        { regenerate: false }
      );
      this.showFirstMenuOnboarding = false;
    },

    async chooseFirstMenuPreferences() {
      this.firstMenuOnboardingSaving = true;
      try {
        await this.completeFirstMenuOnboarding();
        this.$router.push({ name: "my-preferences" });
      } catch (error) {
        console.error("Impossible d’enregistrer l’introduction aux préférences :", error);
      } finally {
        this.firstMenuOnboardingSaving = false;
      }
    },

    async continueToFirstMenus() {
      this.firstMenuOnboardingSaving = true;
      try {
        await this.completeFirstMenuOnboarding();

        if (!Array.isArray(this.menus)) {
          await api.post("/regenerate-my-menus/");
          await this.loadMenus(this.weekNumber);
        }
      } catch (error) {
        this.generationError = error.response?.data?.detail || "La semaine n’a pas pu être validée avec les contraintes actuelles.";
        console.error("Impossible de préparer les premiers menus :", error);
      } finally {
        this.firstMenuOnboardingSaving = false;
      }
    },

    gotoPreferences() {
      this.showWeekPreferences = true;
    },

    async gotoPreviousWeek() {
      this.mealPrepWeekReady = false;
      if (this.menus === "preferences_too_restrictive") return;

      const last = this.weeksInYear;
      this.weekNumber = this.weekNumber === 1 ? last : this.weekNumber - 1;

      await this.loadMenus(this.weekNumber);
      await this.loadBatchCookingPlan();
    },

    async gotoNextWeek() {
      this.mealPrepWeekReady = false;
      if (this.menus === "preferences_too_restrictive") return;

      const last = this.weeksInYear;
      this.weekNumber = this.weekNumber === last ? 1 : this.weekNumber + 1;

      await this.loadMenus(this.weekNumber);
      await this.loadBatchCookingPlan();
    },

    weekStartDate(weekNumber) {
      const jan4 = new Date(this.year, 0, 4);
      const jan4Day = (jan4.getDay() + 6) % 7;
      const mondayWeek1 = new Date(this.year, 0, 4 - jan4Day);
      const start = new Date(mondayWeek1);
      start.setDate(mondayWeek1.getDate() + (Number(weekNumber) - 1) * 7);
      return start;
    },

    weekStartIso(weekNumber) {
      const date = this.weekStartDate(weekNumber);
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },

    weekRangeLabel(weekNumber) {
      const start = this.weekStartDate(weekNumber);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      const months = [
        "janv.", "févr.", "mars", "avr.", "mai", "juin",
        "juil.", "août", "sept.", "oct.", "nov.", "déc.",
      ];
      if (start.getMonth() === end.getMonth()) {
        return `${start.getDate()}–${end.getDate()} ${months[end.getMonth()]}`;
      }
      return `${start.getDate()} ${months[start.getMonth()]}–${end.getDate()} ${months[end.getMonth()]}`;
    },

    capitalizeFirstLetter(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    },

    normalizeWeekNumber(weekNumber) {
      const last = this.weeksInYear;

      if (weekNumber < 1) {
        return last;
      }

      if (weekNumber > last) {
        return 1;
      }

      return weekNumber;
    },

    async hydrateMenuMealimScores(menus) {
      if (!Array.isArray(menus)) return menus;

      const items = menus.flatMap(menu => Array.isArray(menu.items) ? menu.items : []);
      const missingScores = items.filter(item =>
        ["lunch_main", "diner_main"].includes(item.dish_type) &&
        item.dish && item.dish.id != null && item.dish.mealim_score == null
      );
      const ids = [...new Set(missingScores.map(item => String(item.dish.id)))];
      if (!ids.length) return menus;

      const scores = new Map();
      await Promise.all(ids.map(async id => {
        try {
          const response = await api.get(`/recipes/${id}/`);
          if (response.data?.mealim_score != null) {
            scores.set(id, response.data.mealim_score);
          }
        } catch (error) {
          // Preserve the menu if a recipe is temporarily unavailable.
        }
      }));

      for (const item of items) {
        const score = scores.get(String(item.dish?.id));
        if (score != null) this.$set(item.dish, "mealim_score", score);
      }
      return menus;
    },

    async fetchWeekMenus(weekNumber) {
      const normalizedWeekNumber = this.normalizeWeekNumber(weekNumber);
      const response = await api.get(`my-menus/${normalizedWeekNumber}/`);
      const data = Array.isArray(response.data)
        ? await this.hydrateMenuMealimScores(response.data)
        : response.data;

      if (Array.isArray(data)) {
        setCachedWeek(normalizedWeekNumber, data);
      }

      return data;
    },

    async loadMenus(weekNumber) {
      const normalizedWeekNumber = this.normalizeWeekNumber(weekNumber);
      const cachedMenus = getCachedWeek(normalizedWeekNumber);

      // Une semaine déjà chargée s’affiche immédiatement pendant son rafraîchissement.
      this.menus = cachedMenus || "loading";
      this.nextWeekMenusCache = getCachedWeek(this.normalizeWeekNumber(normalizedWeekNumber + 1)) || [];

      try {
        const currentWeekMenus = await this.fetchWeekMenus(normalizedWeekNumber);

        if (normalizedWeekNumber === this.weekNumber) {
          this.menus = currentWeekMenus;
          await this.loadMealPrepRollbackStatus(normalizedWeekNumber);
        }

        // Précharge la semaine suivante sans bloquer l’affichage de la semaine courante.
        this.fetchWeekMenus(normalizedWeekNumber + 1)
          .then((nextWeekMenus) => {
            this.nextWeekMenusCache = Array.isArray(nextWeekMenus) ? nextWeekMenus : [];
          })
          .catch(() => {
            this.nextWeekMenusCache = [];
          });
      } catch (error) {
        if (normalizedWeekNumber === this.weekNumber && !cachedMenus) {
          if (error.response && error.response.status === 404) {
            this.menus = "not_yet_available";
          } else {
            this.menus = "error";
          }
        }
      }
    },

    async checkNextTwoWeeks() {
      const currentWeek = this.normalizeWeekNumber(this.baseWeekNumber);
      const nextWeek = this.normalizeWeekNumber(this.baseWeekNumber + 1);
      const cachedCurrent = getCachedWeek(currentWeek);

      try {
        // Avant, cette vérification rechargeait les mêmes semaines puis loadMenus()
        // les rechargeait encore. On fait maintenant un seul appel par semaine, en parallèle.
        const [currentResult, nextResult] = await Promise.all([
          this.fetchWeekMenus(currentWeek)
            .then((data) => ({ data }))
            .catch((error) => ({ error })),
          this.fetchWeekMenus(nextWeek)
            .then((data) => ({ data }))
            .catch((error) => ({ error })),
        ]);

        if (currentResult.error) {
          if (!cachedCurrent) {
            this.menus =
              currentResult.error.response && currentResult.error.response.status === 404
                ? "preferences_too_restrictive"
                : "error";
          }
          return;
        }

        if (!Array.isArray(currentResult.data) || currentResult.data.length < 7) {
          if (!cachedCurrent) this.menus = "preferences_too_restrictive";
          return;
        }

        this.menus = currentResult.data;

        if (
          !nextResult.error &&
          Array.isArray(nextResult.data) &&
          nextResult.data.length >= 7
        ) {
          this.nextWeekMenusCache = nextResult.data;
        } else {
          this.nextWeekMenusCache = [];
        }
      } catch (e) {
        console.error("Erreur checkNextTwoWeeks :", e);
        if (!cachedCurrent) this.menus = "error";
      }
    },

    getCurrentWeekNumber(date = new Date()) {
      const target = new Date(date.valueOf());
      target.setDate(target.getDate() + 3 - ((target.getDay() + 6) % 7));

      const firstThursday = new Date(target.getFullYear(), 0, 4);
      firstThursday.setDate(
        firstThursday.getDate() + 3 - ((firstThursday.getDay() + 6) % 7)
      );

      return 1 + Math.round(((target - firstThursday) / 86400000 - 3) / 7);
    },

    humanizeDate(isoDate) {
      const date = new Date(isoDate);
      return this.capitalizeFirstLetter(
        date.toLocaleDateString("fr-FR", {
          weekday: "long",
          day: "numeric",
          month: "long",
        })
      );
    },

    gotoDailyMenu() {
      if (this.menus === "preferences_too_restrictive") return;
      this.$router.push({ name: "my-menu" });
    },

    compactDate(isoDate) {
      const date = new Date(isoDate);
      const weekday = this.capitalizeFirstLetter(
        date.toLocaleDateString("fr-FR", { weekday: "short" }).replace(".", "")
      );
      return `${weekday} ${date.getDate()}`;
    },

    isMainMeal(item) {
      return !!item && ["breakfast", "lunch_main", "diner_main"].includes(item.dish_type);
    },

    getSportAdaptations(item) {
      return item?.sport_adaptations || [];
    },

    mediaSrc(uri) {
      if (!uri) return null;
      return `${process.env.VUE_APP_BASE_URL}${uri}`;
    },

    openReplaceModal(item, date) {
      this.itemToReplace = { item, date };
    },

    closeReplaceModal() {
      this.itemToReplace = null;
    },

    protectedChoicesKey() {
      return `mmm-chosen-items:${getHouseholdCacheKey()}:${this.weekStartIso(this.weekNumber)}`;
    },
    getProtectedChoices() {
      const key = this.protectedChoicesKey();
      const saved = this.protectedChoicesByWeek[key] || [];
      try { const stored = JSON.parse(localStorage.getItem(key) || '[]'); return [...new Set([...saved, ...(Array.isArray(stored) ? stored : [])])]; }
      catch (_) { return saved; }
    },
    handleReplacement(item) {
      const chosen = new Set(this.getProtectedChoices());
      [item, ...(item.updated_items || [])].forEach(row => { if (row && row.id) chosen.add(row.id); });
      this.$set(this.protectedChoicesByWeek, this.protectedChoicesKey(), [...chosen]);
      try { localStorage.setItem(this.protectedChoicesKey(), JSON.stringify([...chosen])); } catch (_) { /* Storage unavailable. */ }
      this.weekRefreshPending = true;
      this.weekRefreshMessage = "";
      this.updateMenuItem(item);
      setCachedWeek(this.weekNumber, this.menus);
      this.closeReplaceModal();
    },

    async refreshSelectedWeek(deepSearch = false) {
      if (this.refreshingWeek) return;
      this.refreshingWeek = true;
      this.weekRefreshMessage = "";
      this.deepSearchAvailable = false;
      this.deepSearchRunning = deepSearch;
      const prefsKey = 'mmm-week-preferences-pending:' + getHouseholdCacheKey();
      let applyPreferences = false;
      try { applyPreferences = localStorage.getItem(prefsKey) === "1"; } catch (_) { /* Storage unavailable. */ }
      this.applyingSavedPreferences = applyPreferences;
      try {
        const response = await api.post('/rebalance-my-week/', {
          start_date: this.weekStartIso(this.weekNumber),
          protected_item_ids: this.getProtectedChoices(),
          apply_preferences: applyPreferences,
          deep_search: deepSearch,
        }, { timeout: deepSearch ? 600000 : (applyPreferences ? 180000 : 60000) });
        const menus = await this.fetchWeekMenus(this.weekNumber);
        this.menus = menus;
        this.nextWeekMenusCache = [];
        Object.keys(weekMenusCache).forEach((key) => { delete weekMenusCache[key]; });
        setCachedWeek(this.weekNumber, menus);
        this.weekToolsRevision += 1;
        this.weekRefreshPending = false;
        if (response.data?.recalculated) {
          this.deepSearchAvailable = false;
          try { localStorage.removeItem(prefsKey); } catch (_) { /* Storage unavailable. */ }
          userState.menuRevision += 1;
          this.weekRefreshMessage = "Vos préférences ont été appliquées et la semaine a été recalculée.";
          return;
        }
        const changed = new Set((response.data.replacements || []).map(row => row.menu_item_id)).size;
        const remaining = (response.data.unresolved || []).length;
        const validation = response.data.validation;
        this.weekRefreshMessage = validation && !validation.validated
          ? 'Semaine à rééquilibrer : la règle glucidique de 38 à 57 % n’a pas pu être respectée pour chaque membre. Les ajustements compatibles sont enregistrés, mais la semaine reste à rééquilibrer.'
          : changed
          ? `${changed} plat(s) ajusté(s). ${remaining ? 'Certains apports restent à améliorer ; voir mes apports nutritionnels.' : 'Les objectifs suivis sont atteints.'}`
          : 'Mes choix sont conservés. Aucun meilleur remplacement compatible trouvé ; voir mes apports nutritionnels.';
      } catch (error) {
        this.deepSearchAvailable = !!error.response?.data?.deep_search_available;
        this.weekRefreshMessage = error.response?.data?.detail || "Impossible d’actualiser la semaine. Mes changements restent enregistrés ; je peux réessayer.";
      } finally {
        this.refreshingWeek = false;
        this.applyingSavedPreferences = false;
        this.deepSearchRunning = false;
      }
    },

    gotoMealCard(item, date) {
      if (!item) return;
      this.$router.push({
        name: "my-meal",
        params: { date, meal: item.meal },
      });
    },

    getDailyMenuByDate(date) {
      if (Array.isArray(this.menus)) {
        const currentWeekDailyMenu = this.menus.find((menu) => menu.date === date);
        if (currentWeekDailyMenu) {
          return currentWeekDailyMenu;
        }
      }

      if (Array.isArray(this.nextWeekMenusCache)) {
        const nextWeekDailyMenu = this.nextWeekMenusCache.find((menu) => menu.date === date);
        if (nextWeekDailyMenu) {
          return nextWeekDailyMenu;
        }
      }

      return null;
    },

    getNextDate(dateString) {
      const date = new Date(dateString);
      date.setDate(date.getDate() + 1);
      return date.toISOString().split("T")[0];
    },

    getMealBadgeTypeForItem(dailyMenu, item) {
      if (!dailyMenu || !item) {
        return null;
      }

      if (item.meal_origin_type === "leftovers") {
        return "leftovers";
      }

      const nextDate = this.getNextDate(dailyMenu.date);
      const nextDailyMenu = this.getDailyMenuByDate(nextDate);

      if (["diner_starter", "diner_main"].includes(item.dish_type)) {
        if (nextDailyMenu && nextDailyMenu.lunch_origin_type === "leftovers") {
          return "parent_leftovers";
        }
      }

      if (["lunch_starter", "lunch_main"].includes(item.dish_type)) {
        if (dailyMenu.dinner_origin_type === "leftovers") {
          return "parent_leftovers";
        }
      }

      return null;
    },

    getDisplayMealGroupsFromDailyMenu(dailyMenu) {
      const cards = this.getDisplayCardsFromDailyMenu(dailyMenu);
      const groups = {
        breakfast: [],
        morning_snack: [],
        lunch: [],
        snack: [],
        dinner: [],
      };

      cards.forEach(card => {
        if (card.type === "outside") {
          if (card.meal === "lunch") groups.lunch.push(card);
          if (card.meal === "dinner") groups.dinner.push(card);
          return;
        }

        const dishType = card.item?.dish_type || "";
        if (dishType === "breakfast") groups.breakfast.push(card);
        else if (dishType === "morning_snack") groups.morning_snack.push(card);
        else if (dishType === "snack") groups.snack.push(card);
        else if (dishType.startsWith("lunch_")) groups.lunch.push(card);
        else if (dishType.startsWith("diner_")) groups.dinner.push(card);
      });

      // Garder les 4 emplacements même lorsqu'un repas est absent.
      // L'espace vide est volontaire : il permet d'aligner visuellement
      // Petit déjeuner / Déjeuner / En-cas / Dîner sur les 7 jours.
      return [
        { slot: "breakfast", cards: groups.breakfast },
        { slot: "morning_snack", cards: groups.morning_snack },
        { slot: "lunch", cards: groups.lunch },
        { slot: "snack", cards: groups.snack },
        { slot: "dinner", cards: groups.dinner },
      ];
    },

    getDisplayCardsFromDailyMenu(dailyMenu) {
      const cards = [];
      const items = [...dailyMenu.items];

      const breakfast = items.find((item) => item.dish_type === "breakfast");
      const lunchStarter = items.find((item) => item.dish_type === "lunch_starter");
      const lunchMain = items.find((item) => item.dish_type === "lunch_main");
      const lunchDessert = items.find((item) => item.dish_type === "lunch_dessert");
      const morningSnack = items.find((item) => item.dish_type === "morning_snack");
      const snack = items.find((item) => item.dish_type === "snack");
      const dinnerStarter = items.find((item) => item.dish_type === "diner_starter");
      const dinnerMain = items.find((item) => item.dish_type === "diner_main");
      const dinnerDessert = items.find((item) => item.dish_type === "diner_dessert");

      if (breakfast) {
        cards.push({
          type: "item",
          item: breakfast,
          mealBadgeType: this.getMealBadgeTypeForItem(dailyMenu, breakfast),
        });
      }

      if (morningSnack) {
        cards.push({ type: "item", item: morningSnack, mealBadgeType: this.getMealBadgeTypeForItem(dailyMenu, morningSnack) });
      }

      if (dailyMenu.lunch_origin_type === "outside") {
        cards.push({ type: "outside", meal: "lunch", label: "Déjeuner" });
      } else {
        if (lunchStarter) {
          cards.push({
            type: "item",
            item: lunchStarter,
            mealBadgeType: this.getMealBadgeTypeForItem(dailyMenu, lunchStarter),
          });
        }
        if (lunchMain) {
          cards.push({ type: "item", item: lunchMain, mealBadgeType: this.getMealBadgeTypeForItem(dailyMenu, lunchMain) });
        }
        if (lunchDessert) {
          cards.push({ type: "item", item: lunchDessert, mealBadgeType: this.getMealBadgeTypeForItem(dailyMenu, lunchDessert) });
        }
      }

      if (snack) {
        cards.push({
          type: "item",
          item: snack,
          mealBadgeType: this.getMealBadgeTypeForItem(dailyMenu, snack),
        });
      }

      if (dailyMenu.dinner_origin_type === "outside") {
        cards.push({ type: "outside", meal: "dinner", label: "Dîner" });
      } else {
        if (dinnerStarter) {
          cards.push({
            type: "item",
            item: dinnerStarter,
            mealBadgeType: this.getMealBadgeTypeForItem(dailyMenu, dinnerStarter),
          });
        }
        if (dinnerMain) {
          cards.push({ type: "item", item: dinnerMain, mealBadgeType: this.getMealBadgeTypeForItem(dailyMenu, dinnerMain) });
        }
        if (dinnerDessert) {
          cards.push({ type: "item", item: dinnerDessert, mealBadgeType: this.getMealBadgeTypeForItem(dailyMenu, dinnerDessert) });
        }
      }

      return cards;
    },

    updateMenuItem(item) {
      const updates = Array.isArray(item.updated_items) && item.updated_items.length ? item.updated_items : [item];
      const byId = new Map(updates.map((entry) => [String(entry.id), entry]));
      const patchMenus = (menus) => Array.isArray(menus) ? menus.map((menu) => ({
        ...menu,
        items: menu.items.map((entry) => byId.get(String(entry.id)) || entry),
      })) : menus;
      this.menus = patchMenus(this.menus);
      this.nextWeekMenusCache = patchMenus(this.nextWeekMenusCache);
      Object.keys(weekMenusCache).forEach((key) => { weekMenusCache[key] = patchMenus(weekMenusCache[key]); });
    },

    gotoApports() {
      const firstMenuDate = Array.isArray(this.menus) && this.menus.length
        ? this.menus[0]?.date
        : null;
      this.$router.push({
        name: "apports",
        query: firstMenuDate ? { start_date: firstMenuDate } : {},
      });
    },

    async fetchPat() {
      try {
        const response = await api.get("/my-pat/");
        if (response.data && response.data.pat) {
          this.patData = response.data.pat;
        } else {
          this.patData = null;
        }
      } catch (e) {
        this.patData = null;
      } finally {
        this.loadingPat = false;
      }
    },

    fullImage(url) {
      if (!url) return null;
      return `https://backend-v2.mmmenus.fr${url}`;
    },
  },
};
</script>

<style scoped>
.week-refresh-panel { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 14px; margin: 0 0 20px; padding: 8px 0; border-top: 1px solid #e4e9df; }
.week-refresh-action { display: inline-flex; align-items: center; gap: 7px; border: 0; background: transparent; color: #68765e; padding: 5px 0; font-size: 13px; font-weight: 500; cursor: pointer; }
.week-refresh-action:hover, .week-refresh-action.is-pending { color: #436f0e; text-decoration: underline; }
.week-refresh-action:disabled { opacity: .6; cursor: wait; }
.week-refresh-panel span, .week-refresh-panel p { font-size: 12px; color: #68765e; margin: 0; }
.meal-prep-rollback-banner { display:flex; align-items:center; flex-wrap:wrap; gap:10px 16px; margin:0 0 18px; padding:12px 14px; border:1px solid #d8e4ca; border-radius:10px; background:#f5f8f0; color:#4e5f40; }
.meal-prep-rollback-banner p { margin:0; font-size:13px; }
.meal-prep-rollback-banner button { margin-left:auto; }

.week-dashboard {
  margin-top: 26px;
}

.personalized-week-kicker {
  margin: 0 0 12px;
  color: #6f963a;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: .12em;
  text-transform: uppercase;
}

.first-menu-onboarding {
  z-index: 1300;
}
.first-menu-onboarding .daily-preferences-modal {
  width: min(610px, 100%);
}
.first-menu-onboarding .daily-preferences-modal h2 {
  color: #315b19;
}
.first-menu-onboarding .daily-secondary {
  font-weight: 700;
}

.daily-preferences-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(31, 40, 24, 0.45);
}

.daily-preferences-modal {
  position: relative;
  width: min(520px, 100%);
  padding: 28px;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 18px 60px rgba(0, 0, 0, 0.2);
  text-align: left;
}

.daily-preferences-modal h2 {
  margin: 7px 0 10px;
  font-size: 24px;
}

.daily-preferences-modal p {
  margin: 0;
  color: #5f6658;
  line-height: 1.5;
}

.daily-preferences-close {
  position: absolute;
  top: 10px;
  right: 14px;
  border: 0;
  background: transparent;
  font-size: 28px;
  color: #6b7264;
  cursor: pointer;
}

.daily-preferences-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
}

.daily-secondary {
  border: 1px solid #d7dfce;
  border-radius: 8px;
  padding: 9px 14px;
  background: #fff;
  cursor: pointer;
}

.starter-callout {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin: 18px 0;
  padding: 16px 18px;
  border: 1px solid #dfe8d4;
  border-radius: 14px;
  background: #f8fbf3;
  text-align: left;
}

.starter-callout div {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.starter-callout span {
  color: #5f6658;
}

.starter-callout button {
  flex: 0 0 auto;
  border: 0;
  border-radius: 9px;
  padding: 10px 14px;
  background: #6f963a;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
}

@media (max-width: 700px) {
  .starter-callout {
    align-items: stretch;
    flex-direction: column;
  }
}

.week-intro {
  display: flex;
  justify-content: space-between;
  gap: 30px;
  align-items: flex-end;
  padding: 24px 28px;
  background: #f8fbf3;
  border: 1px solid #e1ebd5;
  border-radius: 18px;
  text-align: left;
}

.week-kicker {
  color: #6f963a;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .12em;
  margin-bottom: 5px;
}

.week-intro h1 {
  margin: 0 0 7px;
  color: #315b19;
  font-size: 30px;
  font-weight: 800;
}

.week-intro p {
  max-width: 650px;
  margin: 0;
  color: #4d5947;
  line-height: 1.45;
}

.week-benefits {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
  min-width: 300px;
}

.week-benefits span {
  background: white;
  border: 1px solid #dbe7cd;
  border-radius: 999px;
  padding: 7px 10px;
  color: #416c26;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.week-benefits-preview {
  align-items: stretch;
}

.week-benefits-preview span {
  min-width: 92px;
  border-radius: 14px;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.week-benefits-preview strong {
  font-size: 20px;
  line-height: 1;
  color: #315b19;
}

.week-benefits-preview small {
  margin-top: 4px;
  font-size: 9px;
  color: #718064;
  text-transform: uppercase;
  letter-spacing: .04em;
}

.week-benefits-preview button {
  border: 1px solid #dbe7cd;
  border-radius: 14px;
  padding: 8px 12px;
  background: #315b19;
  color: white;
  font-size: 11px;
  font-weight: 800;
  cursor: pointer;
}

.nutrition-preview {
  display: block;
}

.nutrition-heading {
  margin-bottom: 18px;
}

.nutrition-heading p {
  max-width: none;
}

.member-tabs {
  margin-top: 14px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.member-tabs button {
  min-width: 88px;
  padding: 8px 15px;
  border: 1px solid #cfdcbe;
  border-radius: 999px;
  background: white;
  color: #526348;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}

.member-tabs button.active {
  background: #315b19;
  border-color: #315b19;
  color: white;
}

.nutrition-dashboard {
  display: grid;
  grid-template-columns: 150px minmax(330px, 1.4fr) minmax(250px, 1fr) auto;
  gap: 18px;
  align-items: center;
}



.protein-need small { color: #7a8474; font-size: 9px; }

.protein-highlight {
  min-height: 112px;
  padding: 14px;
  border-radius: 16px;
  background: white;
  border: 1px solid #dbe7cd;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
}

.nutrition-value {
  color: #315b19;
  font-size: 31px;
  font-weight: 900;
  line-height: 1;
}

.nutrition-value small {
  font-size: 14px;
}

.protein-highlight strong {
  margin-top: 7px;
  color: #263322;
  font-size: 13px;
}

.protein-highlight span {
  margin-top: 2px;
  color: #7a8474;
  font-size: 9px;
}

.macro-panel,
.micro-panel {
  padding: 13px 15px;
  border-radius: 16px;
  background: white;
  border: 1px solid #dbe7cd;
}

.macro-title {
  margin-bottom: 10px;
  color: #526348;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .04em;
}

.macro-bar {
  height: 16px;
  overflow: hidden;
  display: flex;
  border-radius: 999px;
  background: #edf1e8;
}

.macro-segment { height: 100%; }
.macro-protein { background: #527d31; }
.macro-carbs { background: #a9c768; }
.macro-fat { background: #d9b56d; }

.macro-labels {
  margin-top: 11px;
  display: flex;
  gap: 13px;
  flex-wrap: wrap;
  color: #65705f;
  font-size: 10px;
}

.macro-labels span {
  display: flex;
  align-items: center;
  gap: 4px;
}

.macro-labels strong { color: #263322; }

.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  display: inline-block;
}
.protein-dot { background: #527d31; }
.carbs-dot { background: #a9c768; }
.fat-dot { background: #d9b56d; }

.micro-panel {
  display: grid;
  gap: 7px;
}

.micro-title {
  margin-bottom: 2px;
  color: #526348;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .04em;
}

.micro-label {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  color: #65705f;
  font-size: 10px;
}

.micro-label strong { color: #315b19; }

.micro-track {
  height: 6px;
  overflow: hidden;
  border-radius: 999px;
  background: #edf1e8;
}

.micro-fill {
  height: 100%;
  border-radius: inherit;
  background: #789b4a;
}

.nutrition-details {
  align-self: stretch;
  min-width: 135px;
  padding: 12px;
  border: 0;
  border-radius: 14px;
  background: #315b19;
  color: white;
  font-size: 11px;
  font-weight: 800;
  cursor: pointer;
}

.nutrition-preview-note {
  margin-top: 10px;
  color: #8a9384;
  font-size: 9px;
  text-align: right;
}

@media (max-width: 1250px) {
  .nutrition-dashboard {
    grid-template-columns: 140px 1fr 1fr;
  }
  .nutrition-details {
    grid-column: 1 / -1;
    min-height: 38px;
  }
}

@media (max-width: 768px) {
  .nutrition-dashboard {
    grid-template-columns: 1fr;
  }
  .nutrition-details {
    grid-column: auto;
  }
  .nutrition-preview-note {
    text-align: left;
  }
}

.pantry-callout {
  margin: 14px 0 20px;
  padding: 14px 18px;
  border: 1px solid #d8e8c5;
  border-radius: 14px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  text-align: left;
}

.pantry-callout strong,
.pantry-callout span {
  display: block;
}

.pantry-callout strong {
  color: #315b19;
  margin-bottom: 2px;
}

.pantry-callout span {
  color: #66705f;
  font-size: 13px;
}

.pantry-callout button {
  border: 0;
  border-radius: 10px;
  padding: 10px 14px;
  background: #edf2e8;
  color: #6a7662;
  font-weight: 700;
  white-space: nowrap;
}

.pantry-callout small {
  display: block;
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: .08em;
}

.week-planner {
  display: grid;
  grid-template-columns: repeat(7, minmax(125px, 1fr));
  grid-template-rows: auto auto auto auto auto;
  column-gap: 9px;
  row-gap: 0;
  align-items: stretch;
}

.week-day {
  min-width: 0;
  background: #f6f8f3;
  border: 1px solid #e2e8dc;
  border-radius: 14px;
  padding: 7px;
  display: grid;
  grid-row: 1 / span 5;
  grid-template-rows: subgrid;
  row-gap: 7px;
}

.week-day-header {
  grid-row: 1;
  padding: 7px 4px 9px;
  text-align: center;
  color: #315b19;
  font-weight: 800;
  font-size: 16px;
}

.compact-meal-group {
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin: 0;
}
.compact-meal-group:nth-of-type(2) { grid-row: 2; }
.compact-meal-group:nth-of-type(3) { grid-row: 3; }
.compact-meal-group:nth-of-type(4) { grid-row: 4; }
.compact-meal-group:nth-of-type(5) { grid-row: 5; }
.compact-meal-group--empty {
  min-height: 0;
}
.compact-meal-group .compact-meal:last-child {
  margin-bottom: 0;
}

.compact-meal {
  position: relative;
  min-height: 112px;
  margin-bottom: 7px;
  padding: 10px;
  border-radius: 10px;
  background: white;
  border: 1px solid #e6eadf;
  cursor: pointer;
  text-align: left;
  display: flex;
  flex-direction: column;
  transition: box-shadow .15s ease, transform .15s ease;
}

.compact-meal:hover {
  box-shadow: 0 4px 14px rgba(49, 91, 25, .12);
  transform: translateY(-1px);
}

.compact-meal-type {
  color: #789b4a;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .04em;
  margin-bottom: 5px;
}

.compact-meal-content {
  min-width: 0;
}

.compact-meal-content.with-photo {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.compact-meal-photo {
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  border-radius: 8px;
  object-fit: cover;
  background: #f1f3ed;
}

.compact-meal-name {
  color: #263322;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.28;
  overflow-wrap: anywhere;
}

.compact-mealim-score {
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: 4px;
  margin-top: 7px;
  padding: 3px 7px;
  border-radius: 999px;
  background: #f2f6eb;
  color: #587734;
  font-size: 9px;
  line-height: 1.2;
}
.compact-mealim-score strong { font-size: 9px; }

.compact-badge {
  margin-top: 6px;
  color: #52782f;
  font-size: 9px;
  font-weight: 700;
}

.batch-week-panel {
  margin: 14px 0 18px;
  padding: 16px 18px;
  border: 1px solid #d8e8c5;
  border-radius: 14px;
  background: #f7faef;
  text-align: left;
}
.batch-week-heading {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: center;
}
.batch-week-heading h2 {
  margin: 3px 0 4px;
  color: #315b19;
  font-size: 20px;
}
.batch-week-heading p {
  margin: 0;
  color: #616b59;
}
.batch-week-heading > button {
  border: 0;
  border-radius: 10px;
  padding: 10px 13px;
  background: #6f963a;
  color: #fff;
  font-weight: 800;
  cursor: pointer;
  white-space: nowrap;
}
.batch-week-sessions {
  display: grid;
  gap: 12px;
  margin-top: 16px;
}
.batch-week-session {
  padding: 12px;
  border: 1px solid #dfe7d8;
  border-radius: 11px;
  background: #fff;
}
.batch-session-title {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  color: #315b19;
}
.batch-session-title span {
  color: #6c7466;
  font-size: 12px;
}
.batch-week-meal {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #edf1e9;
}
.batch-week-meal-head {
  display: flex;
  justify-content: space-between;
  gap: 14px;
}
.batch-week-meal-head strong {
  color: #263322;
}
.batch-week-meal-head span {
  color: #64705d;
  font-size: 12px;
  font-weight: 700;
}
.batch-week-ingredients {
  display: flex;
  flex-wrap: wrap;
  gap: 7px 14px;
  margin-top: 8px;
  color: #5d6757;
  font-size: 12px;
}
.batch-week-rule {
  padding: 10px 12px;
  border-left: 4px solid #8aad47;
  background: #fff;
  color: #596451;
  font-size: 12px;
}
@media (max-width: 760px) {
  .batch-week-heading,
  .batch-week-meal-head,
  .batch-session-title {
    align-items: stretch;
    flex-direction: column;
  }
}

.compact-batch-badge {
  margin-top: 6px;
  padding: 6px 8px;
  border-radius: 8px;
  background: #eef1e4;
  color: #4f632f;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.25;
}

.compact-sport-badge {
  margin-top: 6px;
  padding: 4px 6px;
  border-radius: 7px;
  background: #eef6e4;
  color: #315b19;
  font-size: 8px;
  line-height: 1.25;
  font-weight: 800;
}

.compact-actions {
  display: flex;
  justify-content: space-between;
  gap: 6px;
  margin-top: auto;
  padding-top: 8px;
  color: #52782f;
  font-size: 10px;
  font-weight: 700;
}

.compact-actions button {
  border: 0;
  padding: 0;
  background: transparent;
  color: #52782f;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.compact-actions button:hover {
  text-decoration: underline;
}

.compact-actions .compact-change {
  color: #74806d;
}

.protein-per-kg {
  margin-top: 5px;
  color: #526348;
  font-size: 10px;
  font-weight: 700;
}

.protein-need {
  margin-top: 8px;
  padding-top: 7px;
  border-top: 1px solid #e5eadf;
  color: #66705f;
  font-size: 10px;
  line-height: 1.35;
  display: grid;
  gap: 2px;
}

.protein-need strong {
  margin: 0;
  color: #263322;
  font-size: 10px;
}

.protein-need b {
  color: #526348;
  font-weight: 700;
}

.compact-meal-outside {
  background: #fbfaf5;
  cursor: default;
}

.compact-meal-outside:hover {
  box-shadow: none;
  transform: none;
}

.outside-mini-icon {
  margin-top: auto;
  color: #789b4a;
}

.week-help {
  margin-top: 14px;
  padding: 10px 14px;
  text-align: center;
  color: #66705f;
  font-size: 13px;
}

.week-help button {
  border: 0;
  padding: 0;
  background: transparent;
  color: #436f0e;
  font-weight: 800;
  text-decoration: underline;
}

@media (max-width: 1180px) and (min-width: 769px) {
  .week-planner {
    grid-template-columns: repeat(4, minmax(145px, 1fr));
    grid-template-rows:
      auto auto auto auto auto
      auto auto auto auto auto;
    column-gap: 9px;
    row-gap: 9px;
  }

  /* Première rangée visuelle : lundi à jeudi */
  .week-day:nth-child(1) { grid-column: 1; grid-row: 1 / span 5; }
  .week-day:nth-child(2) { grid-column: 2; grid-row: 1 / span 5; }
  .week-day:nth-child(3) { grid-column: 3; grid-row: 1 / span 5; }
  .week-day:nth-child(4) { grid-column: 4; grid-row: 1 / span 5; }

  /* Deuxième rangée visuelle : vendredi à dimanche */
  .week-day:nth-child(5) { grid-column: 1; grid-row: 6 / span 5; }
  .week-day:nth-child(6) { grid-column: 2; grid-row: 6 / span 5; }
  .week-day:nth-child(7) { grid-column: 3; grid-row: 6 / span 5; }

  .week-day {
    display: grid;
    grid-template-rows: subgrid;
    row-gap: 7px;
  }
}

@media (max-width: 768px) {
  .week-planner {
    grid-template-rows: none;
  }
  .week-day {
    display: block;
    grid-row: auto;
    grid-column: auto;
  }
  .compact-meal-group {
    margin-bottom: 7px;
  }

  .week-intro,
  .pantry-callout {
    flex-direction: column;
    align-items: stretch;
  }

  .week-benefits {
    justify-content: flex-start;
    min-width: 0;
  }

  .week-planner {
    grid-template-columns: repeat(2, minmax(135px, 1fr));
  }
}

@media (max-width: 480px) {
  .week-planner {
    grid-template-columns: 1fr;
  }
}

.day-selector {
  display: flex;
  justify-content: center;
  gap: 20px;
  align-items: center;
  margin: 0 20px;
}

.day-selector-arrow {
  cursor: pointer;
  background: #436f0e;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
}

.week-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.05;
}

.week-label small {
  margin-top: 4px;
  font-family: "TangoSans";
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.day-selector-previous-day {
  font-size: 20px;
  font-family: "Amanise";
  font-weight: 500;
  color: #85a063;
}

.day-selector-current-day {
  font-size: 30px;
  font-family: "Amanise";
  font-weight: 500;
  color: #436f0e;
}

.day-selector-next-day {
  font-size: 20px;
  font-family: "Amanise";
  font-weight: 500;
  color: #85a063;
}

.day {
  font-size: 30px;
  font-family: "Amanise";
  font-weight: 500;
  color: #436f0e;
}

@media (max-width: 768px) {
  .day {
    text-align: center;
  }
}

.menu-container {
  margin-bottom: 40px;
  flex-direction: column;
}

.menu-grid {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.menu-grid-item {
  display: flex;
}

@media (max-width: 768px) {
  .menu-grid {
    justify-content: center;
  }
}

.menu-item {
  background: white;
  padding: 20px;
  margin: 10px;
  width: 200px;
  min-width: 150px;
  height: 100%;
}

.menu-item-image {
  width: 100%;
  border-radius: 10px;
}

.menu-item-meal {
  font-size: 20px;
  font-weight: 800;
  color: #436f0e;
}

.menu-item-name {
  font-size: 15px;
}

.menu-item-bottom-bar {
  display: flex;
  justify-content: center;
  margin-top: auto;
  padding-top: 10px;
}

.scale-selector {
  display: flex;
  justify-content: center;
}

.scale-selector-day {
  font-size: 20px;
  font-family: "Amanise";
  font-weight: 500;
  color: #85a063;
  cursor: pointer;
  border: 1px solid #436f0e;
  border-right: none;
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
  padding: 10px;
  background: white;
}

.scale-selector-day.active {
  background: #436f0e;
  color: white;
  border: none;
}

.scale-selector-day:hover {
  background: #85a063;
  color: white;
  border: none;
}

.scale-selector-week {
  font-size: 20px;
  font-family: "Amanise";
  font-weight: 500;
  color: #85a063;
  cursor: pointer;
  border: 1px solid #436f0e;
  border-left: none;
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
  padding: 10px;
  background: white;
}

.scale-selector-week.active {
  background: #436f0e;
  color: white;
  border: none;
}

.scale-selector-week:hover {
  background: #85a063;
  color: white;
  border: none;
}

.liked-dish-name {
  font-size: 20px;
  font-weight: bold;
  color: #000;
  text-align: center;
  padding: 10px;
}

.bottom-cards {
  margin-top: 50px;
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.bottom-cards>* {
  flex: 1 1 300px;
  max-width: 100%;
}

.disabled {
  opacity: 0.4;
  pointer-events: none;
}

.pat-section {
  margin-top: 30px;
  padding: 20px 0;
}

.pat-title {
  font-size: 26px;
  margin-bottom: 25px;
  font-weight: bold;
}

.pat-row {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.pat-img {
  width: 150px;
  height: auto;
  border-radius: 6px;
  object-fit: cover;
}

.pat-content h3 {
  margin: 0 0 10px 0;
  font-weight: bold;
}

.pat-content p {
  margin: 0 0 10px 0;
  white-space: pre-line;
}

.pat-link {
  color: #8abd37;
  font-weight: bold;
  text-decoration: none;
}

.pat-link:hover {
  text-decoration: underline;
}

.outside-card {
  background: white;
  padding: 20px;
  margin: 10px;
  width: 200px;
  min-width: 150px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  height: 100%;
}

.outside-card-image {
  width: 100%;
  object-fit: cover;
}

.outside-card-meal {
  font-size: 20px;
  font-weight: 800;
  color: #436f0e;
  margin-top: 12px;
}

.outside-card-name {
  font-size: 15px;
  margin-top: 8px;
}

.outside-card-spacer {
  flex: 1;
  min-height: 20px;
}

.outside-card-bottom-bar {
  display: flex;
  justify-content: center;
  margin-top: auto;
  padding-top: 10px;
}

.outside-card-icon-circle {
  cursor: default;
  background: #8abd37;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
}

/* Hiérarchie simplifiée après revue UX */
.week-label span {
  display: block;
  font-weight: 800;
  line-height: 1.1;
}
.week-label small {
  display: block;
  margin-top: 4px;
  font-family: "TangoSans", Arial, sans-serif;
  font-size: 12px;
  font-weight: 600;
  opacity: 0.72;
}
.week-planner {
  margin-top: 18px;
  margin-bottom: 28px;
}
.compact-meal {
  padding: 11px;
  min-height: 0;
}
.compact-meal-content.with-photo {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.compact-meal-photo {
  width: 100%;
  height: 110px;
  object-fit: cover;
  border-radius: 10px;
}
.compact-meal-type {
  font-size: 13px;
  line-height: 1.25;
}
.compact-meal-name {
  font-size: 14px;
  line-height: 1.3;
  font-weight: 700;
}
.sport-accounted-note {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 10px 0 16px;
  padding: 9px 12px;
  border-radius: 10px;
  background: #f4f8ed;
  color: #466b22;
  font-size: 13px;
}
.sport-accounted-note button {
  border: 0;
  background: transparent;
  color: #466b22;
  text-decoration: underline;
  cursor: pointer;
  font-weight: 700;
  white-space: nowrap;
}
.compact-sport-badge {
  font-size: 11px;
  line-height: 1.3;
}
@media (max-width: 900px) {
  .compact-meal-photo { height: 140px; }
  .sport-accounted-note { align-items: flex-start; flex-direction: column; }
}

.compact-meal-photo--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #edf2e8;
  color: #789b4a;
  font-size: 26px;
}

.pantry-shortcut {
  margin: 4px 0 14px;
  padding: 10px 14px;
  border: 1px solid #dbe7cd;
  border-radius: 12px;
  background: #fbfdf8;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  color: #5f6658;
  font-size: 13px;
  text-align: left;
}
.pantry-shortcut button {
  border: 0;
  background: transparent;
  color: #436f0e;
  font-weight: 800;
  cursor: pointer;
  white-space: nowrap;
  text-decoration: underline;
}

.pantry-tool {
  margin: 14px 0 20px;
  padding: 18px;
  border: 1px solid #d8e8c5;
  border-radius: 16px;
  background: white;
  text-align: left;
}
.pantry-tool-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}
.pantry-tool-heading > div { display: grid; gap: 4px; }
.pantry-tool-heading strong { color: #315b19; font-size: 18px; }
.pantry-tool-heading span { color: #66705f; }
.pantry-tool-heading > button,
.pantry-actions .button {
  border: 0;
  border-radius: 10px;
  padding: 11px 15px;
  background: #6f963a;
  color: white;
  font-weight: 800;
  cursor: pointer;
  white-space: nowrap;
}
.pantry-body { margin-top: 18px; }
.pantry-search label { display: block; margin-bottom: 6px; font-weight: 800; color: #3f542f; }
.pantry-search-box { position: relative; max-width: 580px; }
.pantry-search-box > input {
  width: 100%;
  padding: 11px 12px;
  border: 1px solid #ced9c4;
  border-radius: 10px;
}
.pantry-suggestions {
  position: absolute;
  z-index: 40;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  background: white;
  border: 1px solid #dbe4d3;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(49, 91, 25, .12);
  overflow: hidden;
}
.pantry-suggestions button {
  width: 100%;
  border: 0;
  border-bottom: 1px solid #edf1e9;
  background: white;
  padding: 10px 12px;
  text-align: left;
  display: flex;
  justify-content: space-between;
  gap: 10px;
  cursor: pointer;
}
.pantry-suggestions button:hover { background: #f6faef; }
.pantry-suggestions small { color: #728067; }

.pantry-table {
  margin-top: 4px;
  border: 1px solid #dfe6d8;
  border-radius: 12px;
  overflow: visible;
  background: #fff;
}
.pantry-table-head,
.pantry-table-row {
  display: grid;
  grid-template-columns: minmax(240px, 1fr) minmax(360px, 1.35fr);
  gap: 14px;
}
.pantry-table-head {
  padding: 10px 12px;
  border-bottom: 1px solid #dfe6d8;
  background: #f3f7ed;
  color: #315b19;
}
.pantry-table-row {
  position: relative;
  padding: 12px;
  border-bottom: 1px solid #edf1e9;
}
.pantry-table-row:last-child { border-bottom: 0; }
.pantry-food-cell,
.pantry-details-cell { min-width: 0; }
.pantry-details-cell {
  display: grid;
  grid-template-columns: minmax(90px, .8fr) minmax(85px, .7fr) minmax(125px, 1fr) 34px;
  gap: 7px;
  align-items: start;
}
.pantry-details-cell input,
.pantry-details-cell select,
.pantry-food-cell input {
  width: 100%;
  padding: 9px 10px;
  border: 1px solid #d4ddcc;
  border-radius: 8px;
  background: white;
}
.pantry-food-cell input.selected {
  border-color: #8aad47;
  background: #fbfdf8;
}
.pantry-selected-food {
  display: block;
  margin-top: 5px;
  color: #527d31;
  font-weight: 700;
}
.pantry-remove-row {
  width: 34px;
  height: 36px;
  border: 0;
  border-radius: 8px;
  background: #fff3f1;
  color: #a33c31;
  font-size: 20px;
  cursor: pointer;
}
.pantry-season {
  grid-column: 1 / -1;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 12px;
}
.pantry-row-actions {
  display: flex;
  gap: 10px;
  margin-top: 12px;
}
.pantry-add-row,
.pantry-validate {
  border-radius: 9px;
  padding: 9px 13px;
  font-weight: 800;
  cursor: pointer;
}
.pantry-add-row {
  border: 1px solid #b8c9a6;
  background: white;
  color: #466b22;
}
.pantry-validate {
  border: 0;
  background: #6f963a;
  color: white;
}
.pantry-validate:disabled {
  opacity: .45;
  cursor: not-allowed;
}
.pantry-validation-error {
  margin-top: 8px;
  color: #a33c31;
  font-size: 12px;
  font-weight: 700;
}
.pantry-validated-summary {
  margin-top: 15px;
  padding: 10px 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  border-radius: 9px;
  background: #eef6e4;
  color: #466b22;
}
.pantry-validated-summary span { color: #5f6658; }
.pantry-season.ok { background: #eef6e4; color: #466b22; }
.pantry-season.warning { background: #fff6e6; color: #805b17; }

.pantry-season-reminder {
  margin-top: 14px;
  padding: 11px 13px;
  border-left: 4px solid #8aad47;
  background: #f6f9f1;
  color: #586650;
  font-size: 13px;
  line-height: 1.45;
}
.pantry-season-reminder strong { color: #315b19; }

.pantry-modes { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 16px; }
.pantry-modes label {
  display: flex;
  gap: 10px;
  padding: 13px;
  border: 1px solid #d8e1d0;
  border-radius: 11px;
  cursor: pointer;
}
.pantry-modes label.active { border-color: #7da63d; background: #f3f8eb; }
.pantry-modes span { display: grid; gap: 3px; color: #66705f; font-size: 12px; }
.pantry-modes strong { color: #315b19; font-size: 14px; }
.pantry-actions { display: flex; gap: 10px; margin-top: 16px; }
.pantry-secondary { border: 1px solid #d7dfce; border-radius: 10px; padding: 10px 14px; background: white; cursor: pointer; }
.pantry-result { margin-top: 15px; padding: 13px; border-radius: 11px; background: #eef6e4; color: #315b19; }
.pantry-result-list { display: grid; gap: 5px; margin-top: 8px; font-size: 12px; }

@media (max-width: 820px) {
  .pantry-tool-heading { align-items: stretch; flex-direction: column; }
  .pantry-table-head { display: none; }
  .pantry-table-row { grid-template-columns: 1fr; }
  .pantry-details-cell { grid-template-columns: 1fr 90px 1fr 34px; }
  .pantry-season { grid-column: auto; }
  .pantry-modes { grid-template-columns: 1fr; }
}
</style>
<style scoped>


.week-preferences-text-link { border: 0; padding: 0; background: none; color: #436f0e; font-weight: 700; text-decoration: underline; cursor: pointer; }
.week-preferences-shortcut {
  margin: -4px 0 24px;
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.week-preferences-shortcut > .button,
.week-meal-prep-actions > .button {
  padding: 8px 12px;
  font-size: 13px;
  line-height: 1.25;
  white-space: nowrap;
}
@media (max-width: 650px) {
  .week-preferences-shortcut { gap: 6px; }
  .week-preferences-shortcut > .button,
  .week-meal-prep-actions > .button { padding: 7px 10px; font-size: 12px; }
}
.week-meal-prep-actions{display:flex;align-items:center;flex-wrap:wrap;gap:10px}
.week-preferences-overlay { position: fixed; inset: 0; z-index: 1200; background: rgba(20,35,12,.55); display: flex; align-items: center; justify-content: center; padding: 20px; }
.week-preferences-dialog { width: min(1100px,100%); max-height: 90vh; overflow-y: auto; overscroll-behavior: contain; background: white; border-radius: 16px; }
.week-preferences-header { position: sticky; top: 0; z-index: 3; display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 16px 20px; background: #f4f8ed; border-bottom: 1px solid #dce6d3; }
.week-preferences-header h2 { margin: 0; color: #466b22; font-size: 1.4rem; }
.week-preferences-header button { flex-shrink: 0; }
@media(max-width:650px) { .week-preferences-overlay { padding: 8px; } .week-preferences-dialog { max-height: 95vh; } .week-preferences-header { padding: 12px; } .week-preferences-header h2 { font-size: 1.1rem; } }
.nutrition-fullscreen-overlay { padding: 0; align-items: stretch; }
.nutrition-fullscreen-dialog { width: 100%; max-width: none; height: 100vh; height: 100dvh; max-height: none; border-radius: 0; display: flex; flex-direction: column; overflow: hidden; }
.nutrition-fullscreen-dialog .week-preferences-header { flex: 0 0 auto; }
.nutrition-fullscreen-dialog > .apports-page { flex: 1 1 auto; min-height: 0; overflow-y: auto; overscroll-behavior: contain; }
@media(max-width:650px) { .nutrition-fullscreen-overlay { padding: 0; } .nutrition-fullscreen-dialog { max-height: none; } }

.meal-prep-rollback-actions{display:flex;align-items:center;justify-content:flex-end;flex-wrap:wrap;gap:8px;margin-left:auto}
.meal-prep-rollback-banner button{margin-left:0}
.meal-prep-recalculation-overlay{position:fixed;inset:0;z-index:2500;display:flex;align-items:center;justify-content:center;padding:20px;background:rgba(20,35,12,.7)}
.meal-prep-recalculation-card{width:min(520px,100%);padding:30px 28px;border-radius:18px;background:#fff;text-align:center;box-shadow:0 20px 60px rgba(0,0,0,.22)}
.meal-prep-recalculation-card h2{margin:16px 0 10px;color:#315b19}
.meal-prep-recalculation-card p{margin:8px 0;color:#5f6658;line-height:1.5}
.meal-prep-recalculation-spinner{display:inline-block;width:46px;height:46px;border:5px solid #e5ecd9;border-top-color:#6f9634;border-radius:50%;animation:meal-prep-spin .8s linear infinite}
@keyframes meal-prep-spin{to{transform:rotate(360deg)}}
</style>
